// TESSERAX — BOT HEURISTIQUE DE MESURE (baseline d'équilibrage). Cœur PUR : ni THREE ni DOM.
//
// Le simulateur (core/sim) est un RELECTEUR d'évènements : il ne DÉCIDE rien. Pour CHIFFRER la
// difficulté (durée, score, niveau, cause de mort) par profil de joueur, il faut un PILOTE qui
// observe l'état et choisit un coup. Ce module fournit ce pilote, branché sur le MÊME moteur que
// l'anti-triche (via Sim.feed) → les chiffres produits sont fidèles au jeu réel.
//
// Le bot joue le puits 3D « à plat » (il N'utilise PAS le Pli : empiler dans la seule tranche active
// est une partie valide). Il choisit, pour la pièce courante, l'orientation + la colonne qui MINIMISENT
// une heuristique de stacker classique (trous, hauteur, accidenté) et MAXIMISENT les lignes effacées.
// Trois profils (débutant / moyen / avancé) modulent les poids, le bruit, et l'usage de Zone/Bascule.
//
// PUR & déterministe : aucune dépendance THREE/DOM ; le bruit vient d'une RNG fournie (séparée de la
// RNG de jeu → n'altère pas la séquence de pièces du moteur). C'est de l'OUTILLAGE de mesure, pas du jeu.

import { GRID, HEIGHT } from './constants';
import type { Grid } from './grid';
import { type Dir, settleToward, evaluateClears } from './grid';
import { rotFns, bboxCenter } from './util';
import { CODE } from './replay';
import { type Relic, rarityRank } from './relics';

export interface BotPiece { cells: number[][]; pos: number[]; }

export interface BotWeights {
  lines: number;   // récompense par ligne/étage effacé par le coup
  holes: number;   // pénalité par trou (case vide sous une case pleine)
  agg: number;     // pénalité de hauteur cumulée
  bump: number;    // pénalité d'« accidenté » (écarts de hauteur entre colonnes voisines)
  maxh: number;    // pénalité de la colonne la plus haute (proximité du top-out)
}

export interface BotProfile {
  name: string;
  w: BotWeights;
  noise: number;            // 0..1 : probabilité de jouer un coup aléatoire (joueur imparfait)
  inputRate: number;        // ENTRÉES par SECONDE (modèle de vitesse de main — HYPOTHÈSE, cf. baseline).
                            // Sous gravité, le joueur dispose d'un budget d'inputs limité par pièce ;
                            // en profondeur (chute rapide) le budget fond → placements forcés imparfaits.
  useZone: boolean;         // déclenche la Zone quand la jauge est pleine ?
  zoneBankPieces: number;   // nb de pièces jouées en Zone avant de relâcher (banque)
  useBascule: boolean;      // répare à la Bascule quand le terrain se troue ?
  basculeHoleThreshold: number; // nb de colonnes trouées déclenchant une Bascule de réparation
}

// Profils calibrés à la MAIN (hypothèses — à affiner via la baseline, cf. BAL-MEASURE-001). L'écart
// entre profils tient surtout au BRUIT (imperfection) et à l'usage des outils (Zone/Bascule), pas à des
// poids exotiques : un débutant joue les bons coups par intermittence, un expert presque toujours.
export const BOT_PROFILES: Record<string, BotProfile> = {
  debutant: { name: 'débutant', w: { lines: 2.0, holes: 2.2, agg: 0.25, bump: 0.2, maxh: 0.5 },
    noise: 0.45, inputRate: 3.5, useZone: false, zoneBankPieces: 0, useBascule: false, basculeHoleThreshold: 99 },
  moyen:    { name: 'moyen',    w: { lines: 3.6, holes: 4.5, agg: 0.5,  bump: 0.35, maxh: 0.9 },
    noise: 0.12, inputRate: 6.5, useZone: true, zoneBankPieces: 3, useBascule: true, basculeHoleThreshold: 6 },
  avance:   { name: 'avancé',   w: { lines: 5.0, holes: 6.5, agg: 0.7,  bump: 0.45, maxh: 1.1 },
    noise: 0.0,  inputRate: 11, useZone: true, zoneBankPieces: 5, useBascule: true, basculeHoleThreshold: 4 },
};

/* ===================== géométrie : orientations atteignables ===================== */
// Rotation d'un nuage de cellules — MIROIR EXACT de core/sim.rotate (hors wall-kick / position) :
// rotation autour du centre de la bbox, repli sur grille entière par ré-alignement au coin min.
function rotatedShape(cells: number[][], axis: string): number[][] {
  const oc = bboxCenter(cells);
  let nc = cells.map((k) => { const r = rotFns[axis]([k[0] - oc[0], k[1] - oc[1], k[2] - oc[2]]); return [r[0] + oc[0], r[1] + oc[1], r[2] + oc[2]]; });
  if (!nc.every((k) => Number.isInteger(k[0]) && Number.isInteger(k[1]) && Number.isInteger(k[2]))) {
    const r0 = cells.map(rotFns[axis]);
    const mn = (a: number[][]) => { const m = [1e9, 1e9, 1e9]; for (const k of a) for (let j = 0; j < 3; j++) if (k[j] < m[j]) m[j] = k[j]; return m; };
    const om = mn(cells), nm = mn(r0); nc = r0.map((k) => [k[0] + om[0] - nm[0], k[1] + om[1] - nm[1], k[2] + om[2] - nm[2]]);
  }
  return nc;
}
function normShape(cells: number[][]): number[][] {
  let mx = 1e9, my = 1e9, mz = 1e9;
  for (const c of cells) { if (c[0] < mx) mx = c[0]; if (c[1] < my) my = c[1]; if (c[2] < mz) mz = c[2]; }
  return cells.map((c) => [c[0] - mx, c[1] - my, c[2] - mz]);
}
function shapeKey(cells: number[][]): string { return normShape(cells).map((c) => c.join(',')).sort().join(';'); }

export interface Orientation { axes: string[]; cells: number[][]; }
// Toutes les orientations distinctes atteignables en ≤3 rotations d'axe, avec la SÉQUENCE d'axes qui y
// mène (pour rejouer exactement les mêmes ROT côté moteur). Cellules normalisées au coin min.
export function orientationsOf(baseCells: number[][]): Orientation[] {
  const seen = new Set<string>(); const out: Orientation[] = [];
  const queue: Orientation[] = [{ axes: [], cells: normShape(baseCells) }];
  while (queue.length) {
    const cur = queue.shift()!; const k = shapeKey(cur.cells);
    if (seen.has(k)) continue; seen.add(k); out.push(cur);
    if (cur.axes.length >= 3) continue;
    for (const ax of ['x', 'y', 'z']) queue.push({ axes: [...cur.axes, ax], cells: normShape(rotatedShape(cur.cells, ax)) });
  }
  return out;
}

/* ===================== lecture du terrain ===================== */
export function colHeight(grid: Grid, x: number, z: number): number { for (let y = HEIGHT - 1; y >= 0; y--) if (grid[x][y][z] !== null) return y + 1; return 0; }
export function maxColHeight(grid: Grid): number { let m = 0; for (let x = 0; x < GRID; x++) for (let z = 0; z < GRID; z++) { const h = colHeight(grid, x, z); if (h > m) m = h; } return m; }
export function holeCount(grid: Grid): number {
  let n = 0;
  for (let x = 0; x < GRID; x++) for (let z = 0; z < GRID; z++) { let seen = false; for (let y = HEIGHT - 1; y >= 0; y--) { if (grid[x][y][z] !== null) seen = true; else if (seen) { n++; break; } } }
  return n;
}
function heights(grid: Grid): number[][] { const H: number[][] = []; for (let x = 0; x < GRID; x++) { H[x] = []; for (let z = 0; z < GRID; z++) H[x][z] = colHeight(grid, x, z); } return H; }
function fills(grid: Grid): number[][] { const F: number[][] = []; for (let x = 0; x < GRID; x++) { F[x] = []; for (let z = 0; z < GRID; z++) { let c = 0; for (let y = 0; y < HEIGHT; y++) if (grid[x][y][z] !== null) c++; F[x][z] = c; } } return F; }

/* ===================== évaluation d'un placement ===================== */
interface Candidate { axes: string[]; tx: number; tz: number; cost: number; lines: number; xLines: number; zLines: number; slabs: number; }

// Coût heuristique d'un placement (plus bas = meilleur). On ne CLONE pas la grille : on travaille sur
// les hauteurs/remplissages de colonnes (drop droit ⇒ seule la surface compte) + un test de ligne local.
function evalPlacement(grid: Grid, H: number[][], F: number[][], oriented: number[][], tx: number, tz: number, w: BotWeights, arch?: string): Candidate | null {
  // colonnes occupées par la pièce + cellule la plus basse par colonne (offset relatif).
  let maxRy = 0; for (const c of oriented) if (c[1] > maxRy) maxRy = c[1];
  // drop : L = plus petite élévation telle qu'aucune cellule ne chevauche la surface.
  let L = 0;
  for (const c of oriented) { const need = H[tx + c[0]][tz + c[2]] - c[1]; if (need > L) L = need; }
  if (L + maxRy >= HEIGHT) return null;            // ne rentre pas (top-out si posé) → écarté
  // grille de hauteurs/remplissages après pose (copie légère 7×7).
  const placed: number[][] = oriented.map((c) => [tx + c[0], L + c[1], tz + c[2]]);
  const Hp = H.map((col) => col.slice()), Fp = F.map((col) => col.slice());
  const key = new Set<string>();
  for (const p of placed) { key.add(p[0] + ',' + p[1] + ',' + p[2]); const top = p[1] + 1; if (top > Hp[p[0]][p[2]]) Hp[p[0]][p[2]] = top; Fp[p[0]][p[2]]++; }
  // lignes/étages COMPLÉTÉS par ce coup (test local sur les rangées/étages touchés).
  const isFilled = (x: number, y: number, z: number) => grid[x][y][z] !== null || key.has(x + ',' + y + ',' + z);
  const ys = new Set<number>(); for (const p of placed) ys.add(p[1]);
  let lines = 0, xLines = 0, zLines = 0, slabs = 0;
  for (const y of ys) {
    // étage plein ?
    let layerFull = true; for (let x = 0; x < GRID && layerFull; x++) for (let z = 0; z < GRID; z++) if (!isFilled(x, y, z)) { layerFull = false; break; }
    if (layerFull) { lines += GRID; slabs++; continue; }   // un étage = GRID rangées (poids fort)
    const zs = new Set<number>(), xs = new Set<number>();
    for (const p of placed) if (p[1] === y) { zs.add(p[2]); xs.add(p[0]); }
    for (const z of zs) { let full = true; for (let x = 0; x < GRID; x++) if (!isFilled(x, y, z)) { full = false; break; } if (full) { lines++; xLines++; } }
    for (const x of xs) { let full = true; for (let z = 0; z < GRID; z++) if (!isFilled(x, y, z)) { full = false; break; } if (full) { lines++; zLines++; } }
  }
  // features globales sur Hp/Fp.
  let agg = 0, holes = 0, maxH = 0;
  for (let x = 0; x < GRID; x++) for (let z = 0; z < GRID; z++) { const h = Hp[x][z]; agg += h; if (h > maxH) maxH = h; holes += h - Fp[x][z]; }
  let bump = 0;
  for (let x = 0; x < GRID; x++) for (let z = 0; z < GRID; z++) { if (x + 1 < GRID) bump += Math.abs(Hp[x][z] - Hp[x + 1][z]); if (z + 1 < GRID) bump += Math.abs(Hp[x][z] - Hp[x][z + 1]); }
  let cost = -w.lines * lines + w.holes * holes + w.agg * agg + w.bump * bump + w.maxh * maxH;
  // POLITIQUES PAR ARCHÉTYPE (mesure de diversité de builds, BAL-DOMINANT-001) : on biaise le PLACEMENT
  // pour que le feat de l'archétype s'EXPRIME, afin de mesurer la puissance de l'archétype sans le
  // confond « le bot ne déclenche jamais ce feat ». Ce sont des heuristiques de MESURE, pas du jeu.
  if (arch === 'continuum') {
    // construire vers de GROS clears (≥3 lignes / étage) au lieu de grignoter 1-2 lignes (qui rompent
    // le back-to-back de maîtrise).
    if (lines > 0 && lines < 3 && slabs === 0) cost += 1500;
    if (slabs > 0 || lines >= 3) cost -= 500 * lines;
  } else if (arch === 'croix') {
    // privilégier FORTEMENT une CROIX (un axe X ET un axe Z effacés d'un coup) ; éviter de casser une
    // ligne seule avant qu'une croix se présente.
    if (xLines > 0 && zLines > 0) cost -= 4000;
    else if (lines > 0 && lines < 3) cost += 1000;
  }
  return { axes: [], tx, tz, cost, lines, xLines, zLines, slabs };
}

export interface BotPlan { axes: string[]; targetX: number; targetZ: number; lines: number; }

// Choisit le meilleur placement de la pièce courante (orientation + colonne min visée). `rand` injecte
// le bruit du profil (joueur imparfait). Renvoie null si aucun placement ne rentre (terrain saturé).
export function planPlacement(grid: Grid, current: BotPiece, w: BotWeights, noise: number, rand: () => number, arch?: string): BotPlan | null {
  const H = heights(grid), F = fills(grid);
  const oris = orientationsOf(current.cells);
  const cands: Candidate[] = [];
  for (const o of oris) {
    let maxX = 0, maxZ = 0; for (const c of o.cells) { if (c[0] > maxX) maxX = c[0]; if (c[2] > maxZ) maxZ = c[2]; }
    for (let tx = 0; tx + maxX < GRID; tx++) for (let tz = 0; tz + maxZ < GRID; tz++) {
      const e = evalPlacement(grid, H, F, o.cells, tx, tz, w, arch);
      if (e) { e.axes = o.axes; cands.push(e); }
    }
  }
  if (!cands.length) return null;
  cands.sort((a, b) => a.cost - b.cost);
  // Bruit : un joueur imparfait choisit parfois un coup VRAIMENT sous-optimal — pioché UNIFORMÉMENT
  // dans tous les placements légaux (y compris ceux qui enterrent un trou ou empilent dangereusement).
  // C'est ce qui distingue un débutant (bruit élevé → trous, top-out précoce) d'un expert (bruit nul).
  let pick = cands[0];
  if (noise > 0 && rand() < noise) pick = cands[(rand() * cands.length) | 0];
  return { axes: pick.axes, targetX: pick.tx, targetZ: pick.tz, lines: pick.lines };
}

/* ===================== Bascule de réparation ===================== */
function cloneGrid(grid: Grid): Grid { return grid.map((p) => p.map((c) => c.slice())); }
const DIRS: Dir[] = ['nx', 'px', 'nz', 'pz'];
// Direction de Bascule qui RÉDUIT le plus le nombre de trous (ou null si aucune n'améliore). On simule
// settleToward sur une copie (la Bascule peut aussi déclencher des effacements → on les retire avant de
// compter, pour juger la réparation réelle). Outillage de mesure : correct prime sur rapide.
export function bestBasculeDir(grid: Grid): Dir | null {
  const before = holeCount(grid);
  let best: Dir | null = null, bestHoles = before;
  for (const d of DIRS) {
    const g = cloneGrid(grid); settleToward(g, d);
    // retire d'éventuelles lignes/étages complétés par le glissement (sinon on sous-compte les trous).
    while (true) { const ev = evaluateClears(g); if (ev.rowCount === 0 && ev.fullLayers.length === 0) break; for (const k of ev.rowCells) g[k[0]][k[1]][k[2]] = null; for (const y of ev.fullLayers) for (let x = 0; x < GRID; x++) for (let z = 0; z < GRID; z++) g[x][y][z] = null; settleDownLocal(g); }
    const h = holeCount(g);
    if (h < bestHoles) { bestHoles = h; best = d; }
  }
  return best;
}
function settleDownLocal(grid: Grid): void {
  for (let x = 0; x < GRID; x++) for (let z = 0; z < GRID; z++) { let w = 0; for (let y = 0; y < HEIGHT; y++) { const v = grid[x][y][z]; if (v !== null) { grid[x][y][z] = null; grid[x][w][z] = v; w++; } } }
}

/* ===================== draft de reliques (mesure de dominance) ===================== */
// Indice de la relique la plus VALORISÉE d'une offre, pour un bot qui CONSTRUIT un deck (mesure du
// plafond de puissance, BAL-DOMINANT-001). Heuristique simple : rareté + bonus pour les tags qui
// poussent le SCORE/la Zone/le combo, + bonus de SYNERGIE avec le build déjà acquis (les decks
// s'auto-renforcent — c'est précisément ce qu'on cherche à quantifier).
export function pickRelic(offer: Relic[], ownedTags: string[]): number {
  const fav = new Set(['score', 'zone', 'combo', 'bascule', 'risque']);
  let best = 0, bestV = -1e9;
  for (let i = 0; i < offer.length; i++) {
    const r = offer[i];
    let v = rarityRank(r.rarity) + 1;
    for (const t of r.tags) { if (fav.has(t)) v += 1; if (ownedTags.includes(t)) v += 0.6; }
    if (v > bestV) { bestV = v; best = i; }
  }
  return best;
}

/* ===================== codes d'évènements (pour le pilote) ===================== */
export const ROT_CODE: Record<string, number> = { x: CODE.ROT_X, y: CODE.ROT_Y, z: CODE.ROT_Z };
export const BASC_CODE: Record<string, number> = { px: CODE.BASC_PX, nx: CODE.BASC_NX, pz: CODE.BASC_PZ, nz: CODE.BASC_NZ };
